/**
 * Generates an internal EAN-13 barcode starting with prefix "20"
 * The barcode format: 20 + 10 random digits + 1 checksum digit = 13 digits total
 *
 * @returns A valid 13-digit EAN-13 barcode string
 */
export function generateInternalEan(): string {
  // Prefix for internal use
  const prefix = '20';

  // Generate 10 random digits
  let randomDigits = '';
  for (let i = 0; i < 10; i++) {
    randomDigits += Math.floor(Math.random() * 10).toString();
  }

  // Combine prefix and random digits (12 digits total so far)
  const partialEan = prefix + randomDigits;

  // Calculate the 13th digit (checksum) using EAN-13 Modulo 10 algorithm
  const checksum = calculateEan13Checksum(partialEan);

  // Return the complete 13-digit EAN
  return partialEan + checksum;
}

/**
 * Calculates the checksum digit for an EAN-13 barcode
 * Algorithm:
 * 1. Sum the digits at odd positions (1, 3, 5, ...) multiplied by 1
 * 2. Sum the digits at even positions (2, 4, 6, ...) multiplied by 3
 * 3. Add both sums
 * 4. The checksum is: (10 - (sum % 10)) % 10
 *
 * @param partialEan - The first 12 digits of the EAN (without checksum)
 * @returns The checksum digit (0-9)
 */
function calculateEan13Checksum(partialEan: string): string {
  if (partialEan.length !== 12) {
    throw new Error('Partial EAN must be exactly 12 digits');
  }

  let sum = 0;

  // Process each digit
  for (let i = 0; i < 12; i++) {
    const digit = parseInt(partialEan[i], 10);

    // Positions are 1-indexed in EAN-13 algorithm
    // Odd positions (1, 3, 5, 7, 9, 11) multiply by 1
    // Even positions (2, 4, 6, 8, 10, 12) multiply by 3
    if ((i + 1) % 2 === 1) {
      // Odd position (1-indexed)
      sum += digit * 1;
    } else {
      // Even position (1-indexed)
      sum += digit * 3;
    }
  }

  // Calculate checksum: (10 - (sum % 10)) % 10
  const checksum = (10 - (sum % 10)) % 10;

  return checksum.toString();
}
